import { useState } from 'react'
import { Spinner } from '@heroui/react'
import { MonthPeriodPicker } from '../reports/shared/MonthPeriodPicker'
import { ChartFormatPicker } from '../reports/shared/ChartFormatPicker'
import { GrafHeaderMin } from '../reports/shared/GrafHeaderMin'
import { ChartFrame, withJsonFormat } from '../reports/shared/ChartFrame'
import { MeasDataGrid } from './MeasDataGrid'
import { TableOrientationToggle } from '../reports/shared/TableOrientationToggle'
import { useDayPeriodStore } from '../../store/useDayPeriodStore'
import { useMonthPeriodStore } from '../../store/useMonthPeriodStore'
import { useTimeZoneStore } from '../../store/useTimeZoneStore'
import { useTableOrientationStore } from '../../store/useTableOrientationStore'
import { useMeasStore, useHlyMeasStore, useDlyMeasStore } from '../../store/useMeasStore'
import { useMeasTableData } from '../../hooks/useMeasTableData'
import { buildStationsChartUrl } from '../../hooks/useMeasChartData'
import { useChartsToFill } from '../../hooks/shared/useToFill'
import { unionNodeGhIds } from '../../utils/computeGroupTree'
import { MAX_STATIONS_PER_CHART } from '../../config/measConfig'
import { useAuthStore } from '../../store/useAuthStore'

const CHART_BASE = import.meta.env.VITE_API_URL || '/api'

function today() {
  const now = new Date()
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`
}

// Rekurzivně posbírá "sekce grafu" — jeden list stromu = jedna sekce s vlastním grafem, nadpis se
// generuje na každé mezilehlé úrovni sestupu (viz doc/plans/station-group-meas.md sekce 4a).
// depth 0 = přímo vybraný uzel/root uzel (velký nadpis), hlubší úrovně = menší podnadpisy.
function collectGraphSections(node, depth, path) {
  if (!node.children || node.children.length === 0) {
    return [{ node, depth, headings: path }]
  }
  return node.children.flatMap(child => collectGraphSections(child, depth + 1, [...path, child.label]))
}

function graphSectionsForRoots(nodes) {
  return nodes.flatMap(node => collectGraphSections(node, 0, [node.label]))
}

function GraphHeading({ text, depth }) {
  const cls = depth === 0
    ? 'text-base font-semibold text-gray-800'
    : 'text-sm font-medium text-gray-600'
  return <div className={`${cls} px-1 pt-3 pb-1`}>{text}</div>
}

export function MeasGrafTable({ nodes, measDef, stationsByGhId, isSingleLeaf }) {
  const { date, setDate } = useDayPeriodStore()
  const { year, month, setYear, setMonth } = useMonthPeriodStore()
  const tz = useTimeZoneStore(s => s.tz)
  // windowDays/alignEnd/isAktual/chartFormat/interactive jsou oddělené per granularitě (10m/HLY/DLY),
  // stejně jako u jedné stanice (useGrafStore/useHlyGrafStore/useDlyGrafStore) — viz useMeasStore.js.
  // activeTab zůstává sdílený napříč granularitami (obecná UI preference, ne stav vázaný na data).
  const store10m = useMeasStore()
  const storeHly = useHlyMeasStore()
  const storeDly = useDlyMeasStore()
  const { activeTab, setActiveTab } = store10m
  const token = useAuthStore(s => s.token)
  const orientSlug = `meas-${measDef.q}`
  const orientation = useTableOrientationStore(s => s.getOrientation(orientSlug))
  // Gate potvrzení (sekce 4a-i) — záměrně lokální, ne v useMeasStore/sessionStorage: MeasGrafTable
  // se remountuje/překreslí s novým "nodes" pokaždé, když se změní vybraný uzel, skupina nebo
  // filtry, takže se toto samo vynuluje bez nutnosti dopočítávat a invalidovat nějaký klíč.
  const [isConfirmed, setIsConfirmed] = useState(false)

  const granKey = measDef.granKey
  const grStore = granKey === 'dly' ? storeDly : granKey === 'hly' ? storeHly : store10m
  const { isAktual, setIsAktual, chartFormat, interactive } = grStore
  const { windowDays, setWindowDays, alignEnd, setAlignEnd } = grStore

  function handleDayChange(newDate) {
    setDate(newDate)
    setIsAktual(false)
  }

  function handleAktual() {
    setDate(today())
    setIsAktual(true)
  }

  function handleMonthChange(setter) {
    return value => {
      setter(value)
      setIsAktual(false)
    }
  }

  const graphSections = graphSectionsForRoots(nodes)
  const totalGraphs = graphSections.length
  const allGhIds = [...new Set(nodes.flatMap(n => [...unionNodeGhIds(n)]))]

  // Gate (sekce 4a-i): klik na uzel nad úrovní listu se nejdřív jen shrne — teprve po potvrzení se
  // skutečně vykreslí grafy/tabulka. Klik na list gatem neprochází.
  const confirmed = isSingleLeaf || isConfirmed

  const dateOrYearMonth = granKey === 'dly'
    ? { year, month, isAktual }
    : { dateStr: date, windowDays, alignEnd, isAktual }

  // Grafy se vždy stahují jako json[<fmt>,meta] (kvůli meta.toFill). Klíč grafu = URL bez
  // cache-bustu; každý graf se přenačte sám, až se dotáhnou všechny jeho řady.
  const charts = graphSections.map(section => {
    const ghIds = [...unionNodeGhIds(section.node)].slice(0, MAX_STATIONS_PER_CHART)
    const chartKey = withJsonFormat(buildStationsChartUrl(CHART_BASE, ghIds, measDef, dateOrYearMonth, tz, null), chartFormat)
    return { section, chartKey }
  })
  const screenKey = JSON.stringify(['meas-graf', measDef.q, granKey, allGhIds, dateOrYearMonth, tz])
  const { setMeta, bust } = useChartsToFill(
    screenKey,
    charts.map(c => c.chartKey),
    { gran: granKey === 'dly' ? 'DLY' : granKey === 'hly' ? 'HLY' : '10M', live: isAktual },
    confirmed && activeTab === 'graf'
  )

  const { dataMap, labelMap, dayMap, times, isLoading, isError } = useMeasTableData(
    allGhIds, measDef.tsId, granKey, dateOrYearMonth, confirmed && activeTab === 'data'
  )

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="p-4 border-b border-gray-200 bg-white flex flex-col gap-3">
        {(granKey === '10m' || granKey === 'hly') && (
          <GrafHeaderMin
            obsType={granKey === '10m' ? '10M' : 'HLY'}
            date={date}
            onDayChange={handleDayChange}
            isAktual={isAktual}
            onAktual={handleAktual}
            windowDays={windowDays}
            setWindowDays={setWindowDays}
            alignEnd={alignEnd}
            setAlignEnd={setAlignEnd}
            useStore={granKey === '10m' ? useMeasStore : useHlyMeasStore}
            tzTrailing={activeTab === 'data' ? <TableOrientationToggle slug={orientSlug} /> : null}
          />
        )}
        {granKey === 'dly' && (
          <div className="flex flex-wrap items-start gap-4">
            <MonthPeriodPicker
              year={year}
              month={month}
              onYearChange={handleMonthChange(setYear)}
              onMonthChange={handleMonthChange(setMonth)}
              obsType="DLY"
              isAktual={isAktual}
              onAktual={() => setIsAktual(true)}
            />

            <div className="flex flex-col gap-1 ml-auto">
              <span className="text-xs text-gray-400 font-medium uppercase tracking-wide">
                {activeTab === 'data' ? 'Tabulka' : 'Formát'}
              </span>
              {activeTab === 'data'
                ? <TableOrientationToggle slug={orientSlug} />
                : <ChartFormatPicker useStore={useDlyMeasStore} />}
            </div>
          </div>
        )}

        <div className="flex gap-2 border-b border-gray-100 -mb-4 pb-0">
          {['graf', 'data'].map(tab => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`px-4 py-1.5 text-sm font-medium border-b-2 transition-colors ${
                activeTab === tab
                  ? 'border-blue-600 text-blue-700'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              {tab === 'graf' ? 'Graf' : 'Data'}
            </button>
          ))}
        </div>
      </div>

      {/* Obsah */}
      <div className="flex-1 min-h-0 p-4 flex flex-col overflow-y-auto">
        {!confirmed && (
          <div className="flex flex-col gap-3 items-start bg-amber-50 border border-amber-200 rounded-lg p-4 text-sm text-amber-800">
            <p>
              Tento výběr obsahuje <strong>{totalGraphs}</strong> {totalGraphs === 1 ? 'graf' : totalGraphs < 5 ? 'grafy' : 'grafů'} a{' '}
              <strong>{allGhIds.length}</strong> {allGhIds.length === 1 ? 'časovou řadu' : allGhIds.length < 5 ? 'časové řady' : 'časových řad'}.
              Načtení může být náročné a chvíli trvat.
            </p>
            <button
              onClick={() => setIsConfirmed(true)}
              className="px-3 py-1.5 rounded-lg bg-amber-600 text-white text-xs font-medium hover:bg-amber-700"
            >
              Zobrazit i přesto
            </button>
          </div>
        )}

        {confirmed && activeTab === 'graf' && (
          <div className="flex flex-col">
            {charts.map(({ section, chartKey }, i) => {
              const truncated = unionNodeGhIds(section.node).size > MAX_STATIONS_PER_CHART
              const url = chartKey + bust(chartKey)
              return (
                <div key={i}>
                  {section.headings.map((h, hi) => (
                    <GraphHeading key={hi} text={h} depth={hi} />
                  ))}
                  {truncated && (
                    <p className="text-xs text-amber-700 px-1 pb-1">
                      Zobrazeno prvních {MAX_STATIONS_PER_CHART} z {unionNodeGhIds(section.node).size} stanic — graf podporuje max {MAX_STATIONS_PER_CHART}.
                    </p>
                  )}
                  <div className="overflow-auto px-1 pb-2">
                    <ChartFrame
                      key={`${url}:${token ?? 'anon'}`}
                      url={url}
                      gran={granKey === 'dly' ? 'dly' : undefined}
                      interactive={interactive}
                      onMeta={meta => setMeta(chartKey, meta)}
                    />
                  </div>
                </div>
              )
            })}
          </div>
        )}

        {confirmed && activeTab === 'data' && (
          <>
            {isLoading && (
              <div className="flex items-center gap-2 text-sm text-gray-500">
                <Spinner size="sm" /> Načítání dat…
              </div>
            )}
            {isError && <p className="text-red-500 text-sm">Chyba při načítání dat</p>}
            {!isLoading && !isError && (
              <MeasDataGrid nodes={nodes} stationsByGhId={stationsByGhId} dataMap={dataMap} labelMap={labelMap} dayMap={dayMap} times={times} unit={measDef.unit} orientation={orientation} granKey={granKey} />
            )}
          </>
        )}
      </div>
    </div>
  )
}
